import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Film, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, mediaUrl } from "@/lib/api";
import { PageHead } from "@/admin/AdminLayout";

export default function Media() {
  const qc = useQueryClient();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("");
  const { data = [] } = useQuery({ queryKey: ["admin-media", kind], queryFn: async () => (await api.get("/admin/media", { params: { kind } })).data });
  const remove = useMutation({ mutationFn: (id) => api.delete(`/admin/media/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-media"] }) });
  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      await api.post("/admin/media/upload?folder=library", fd);
      toast.success("Upload complete");
      qc.invalidateQueries({ queryKey: ["admin-media"] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const copy = (u) => { navigator.clipboard.writeText(mediaUrl(u)); toast.success("URL copied"); };
  return (
    <div data-testid="admin-media">
      <PageHead title="Media">
        <select className="field !w-auto" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="media-kind-filter"><option value="">All</option><option value="image">Images</option><option value="video">Videos</option></select>
      </PageHead>
      <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }} onClick={() => ref.current?.click()}
        className="flex cursor-pointer flex-col items-center gap-2 border border-dashed border-white/20 p-10 text-center hover:border-white/40" data-testid="media-dropzone">
        <Upload className="text-[#8b95a5]" />
        <p className="text-sm text-[#c7ccd4]">{busy ? "Uploading…" : "Drop images (auto WebP) or videos (MP4 / WebM, up to 200 MB)"}</p>
        <input ref={ref} type="file" hidden multiple accept="image/*,video/mp4,video/webm,video/quicktime" onChange={(e) => upload(e.target.files)} data-testid="media-input" />
      </div>
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {data.map((m) => (
          <div key={m.id} className="admin-card group overflow-hidden" data-testid={`media-item-${m.id}`}>
            <div className="relative aspect-[4/3] bg-[#0e1013]">
              {m.kind === "image" ? <img src={mediaUrl(m.thumb || m.url)} alt="" className="h-full w-full object-cover" /> : <video src={mediaUrl(m.url)} className="h-full w-full object-cover" muted playsInline preload="metadata" />}
              {m.kind === "video" && <Film size={14} className="absolute left-2 top-2 text-white" />}
            </div>
            <div className="flex items-center justify-between p-2">
              <span className="truncate text-[0.65rem] text-[#8b95a5]">{m.original_filename || m.kind}</span>
              <div className="flex gap-1">
                <button onClick={() => copy(m.url)} className="p-1 text-[#8b95a5] hover:text-white" title="Copy URL"><Copy size={13} /></button>
                <button onClick={() => remove.mutate(m.id)} className="p-1 text-[#8b95a5] hover:text-red-400"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
        {!data.length && <p className="text-sm text-[#8b95a5]">No media yet.</p>}
      </div>
    </div>
  );
}
